import { AutomationRuleEntry } from 'custom-type';
import { isFollowGateAllowed, limitAsNumber } from '../../../../../core/utility';

/* ---------------------------------------------------------------------------
   GOAL TEMPLATES — "what do you want this Zap to do?"

   The rule form asks the creator to express an outcome ("get more followers")
   in ClanConnect's own mechanics: a keyword, an opener card, a follow mode, a
   DM type. Each disclosure unfolds another form, so the creator has to learn
   the machinery before they can say what they want.

   A goal template inverts that. The creator picks the outcome; the template
   already knows which combination of rule_type / dm_flow_mode / opener copy
   produces it, and asks only for the two or three things that are genuinely
   theirs — the keyword, the link, the message.

   Nothing here is new capability: every template is a preset over the existing
   AutomationRuleEntry, so what it builds is exactly what the full form would
   have built by hand (which is what "Customise details" then opens, pre-filled).

   Kept identical to the mobile copy at
   clanconnect_mobile/modules/influencer/components/zap/zap-automations/ig-dm-automations/zapGoalTemplates.ts
   — edit both together.
   --------------------------------------------------------------------------- */

/* Copy reused from the full form's presets (StepConfigureRules), so a templated
   rule and a hand-built one read the same in the DM. */
const OPENER_MESSAGE = "Hi there! Thanks for your interest 😊 Tap below and I'll send the link right over ✨";
const OPENER_BUTTON = 'Send Link Now';
const FOLLOW_ASK = "Thanks for your comment! 😊 Make sure you're following me so I can send you the link.";

/** Matches the server's DEFAULT_SUCCESS_MESSAGE (zap-forms.ts). */
export const DEFAULT_FORM_SUCCESS = 'Thanks! We have got your details.';

/** The button on a lead-collecting DM. Neutral enough for a waitlist, an
    enquiry or an order — "Sign up" only fits the first. */
export const DEFAULT_SIGNUP_BUTTON = 'Get started';

/** The DM body every goal starts from. Seeded into the answers rather than
    applied as a silent fallback, so the creator can see and change the words
    that will actually be sent. */
export const DEFAULT_DM_MESSAGE = 'Here you go! 🙌';

/* Growth used to sit here as a sixth outcome, which made it look like an
   alternative to selling or collecting leads rather than something you do
   alongside them. It is now a step of its own between the goal and the posts. */
export type ZapGoalId = 'link' | 'sell' | 'faq' | 'leads';

export type ZapGoalQuestionId =
  | 'keywords'
  | 'follow_gate'
  | 'opener'
  | 'delivery'
  | 'signup'
  | 'replies'
  | 'products'
  | 'review';

/* 'opener', 'delivery' and 'replies' each cover one PART OF THE CONVERSATION
   rather than one database column — the card they tap, the message they get,
   the public reply — so every field the old form had is asked for, without one
   screen per field. */
export type ZapGoalQuestionKind =
  | 'keywords'
  | 'choice'
  | 'opener'
  | 'delivery'
  | 'signup'
  | 'replies'
  | 'products'
  | 'review';

export interface ZapGoalChoice {
  value: string;
  title: string;
  description: string;
}

export interface ZapGoalQuestion {
  id: ZapGoalQuestionId;
  kind: ZapGoalQuestionKind;
  /** Asked as a question, in the creator's words. */
  title: string;
  /** One line under the title explaining why it is being asked. */
  help?: string;
  placeholder?: string;
  /** Tap-to-fill chips. For 'keywords' they are single words; for text, whole messages. */
  suggestions?: string[];
  choices?: ZapGoalChoice[];
  /** Next stays enabled with this unanswered. */
  optional?: boolean;
  /** 'delivery' only: this goal is about the link, so it must be filled in. */
  linkRequired?: boolean;
  /** One or two words for the stepper, so the whole journey is visible up front. */
  stepLabel: string;
}

/**
 * Everything a templated rule is made of.
 *
 * The opener copy, the button label and the follow-up ask used to be constants
 * applied at build time, which made them unreachable outside the full form.
 * They are answers with sensible defaults instead, so the guided flow can edit
 * every field the old form could — presets, not restrictions.
 */
export interface ZapGoalAnswers {
  keywords: string[];
  /* Asking them to follow is a MODIFIER, not an outcome of its own: selling a
     product or collecting a lead can gate on a follow just as well as the
     Grow-my-followers shortcut does. 'off' sends to everyone. */
  follow_gate: 'off' | 'soft' | 'hard';
  message: string;
  /** Comment replies posted publicly under their comment. More than one = rotated. */
  comment_replies: string[];
  /** Tappable button under the delivered message. */
  link_url: string;
  link_label: string;
  /** The card that arrives first, before they tap. */
  opener_message: string;
  opener_button_label: string;
  /** Shown when the rule asks them to follow. */
  follow_ask_message: string;
  /* Two extra buttons Instagram allows on a product card, on top of the product
     link itself — typically the creator's storefront and link-in-bio. Each is
     all-or-nothing: a label without a link (or the reverse) is dropped, and the
     second only renders when the first is filled, because buttons render in
     order and a lone "third" would silently become the second. */
  zap_cta_label: string;
  zap_cta_url: string;
  zap_cta2_label: string;
  zap_cta2_url: string;

  /* COLLECT SIGN-UPS — either the creator already has a form somewhere, or they
     build one here. In 'form' mode the link is not typed at all: the form is
     saved on Create and its public /form/:slug URL becomes the button's link. */
  signup_mode: 'link' | 'form';
  form_title: string;
  form_description: string;
  form_fields: ZapFormFieldDraft[];
  /** Shown on the form after they submit. Generic by default — a form is not
      always a waitlist. */
  form_success_message: string;
  /** Set once the form has been saved, so editing updates it instead of making another. */
  zap_form_id?: number;
  form_slug?: string;
}

/** A question in the form builder. Mirrors the server's field contract. */
export interface ZapFormFieldDraft {
  key: string;
  label: string;
  type: 'text' | 'long_text' | 'email' | 'phone' | 'instagram';
  required: boolean;
  placeholder?: string;
}

/* The presets the builder offers as one-tap chips. Each knows its own keyboard,
   validation and placeholder, so "Email" is never just a text box. */
export const FORM_FIELD_PRESETS: ZapFormFieldDraft[] = [
  { key: 'name',      label: 'Name',             type: 'text',      required: true,  placeholder: 'Your name' },
  { key: 'email',     label: 'Email',            type: 'email',     required: true,  placeholder: 'you@example.com' },
  { key: 'phone',     label: 'Phone',            type: 'phone',     required: false, placeholder: 'Your number' },
  { key: 'instagram', label: 'Instagram handle', type: 'instagram', required: false, placeholder: '@yourhandle' },
];

/** What a brand-new form starts with — the two things every lead needs. */
export const defaultFormFields = (): ZapFormFieldDraft[] =>
  FORM_FIELD_PRESETS.filter((f) => f.key === 'name' || f.key === 'email').map((f) => ({ ...f }));

export const emptyGoalAnswers = (): ZapGoalAnswers => ({
  keywords: [],
  follow_gate: 'off',
  message: DEFAULT_DM_MESSAGE,
  comment_replies: [],
  link_url: '',
  link_label: 'Open link',
  opener_message: OPENER_MESSAGE,
  opener_button_label: OPENER_BUTTON,
  follow_ask_message: FOLLOW_ASK,
  zap_cta_label: '',
  zap_cta_url: '',
  zap_cta2_label: '',
  zap_cta2_url: '',
  signup_mode: 'link',
  form_title: '',
  form_description: '',
  form_fields: defaultFormFields(),
  form_success_message: DEFAULT_FORM_SUCCESS,
});

export interface ZapGoalTemplate {
  id: ZapGoalId;
  /** Card title — the outcome, in the creator's words. */
  title: string;
  /** Card subtitle — what actually happens, in one line. */
  tagline: string;
  /** Entitlement this template needs; undefined = every plan. */
  requires?: 'follow_gate' | 'zap_link' | 'zap_forms';
  questions: ZapGoalQuestion[];
}

export const KEYWORD_SUGGESTIONS = ['LINK', 'INFO', 'PRICE', 'GUIDE', 'SEND'];

export const MESSAGE_SUGGESTIONS = [
  "Here you go! 🙌 Everything you asked for is below.",
  "Thanks for waiting — here's the link I promised ✨",
];

export const OPENER_MESSAGE_SUGGESTIONS = [
  OPENER_MESSAGE,
  "Hey! So glad you're here 😊 Tap below and I'll send it across ✨",
  "Tap below and I'll send it over 👇",
];

export const OPENER_BUTTON_SUGGESTIONS = ['Send Link Now', 'Send me the link', 'Yes, send it over'];

export const FOLLOW_ASK_SUGGESTIONS = [
  FOLLOW_ASK,
  "Almost there — follow me and tap below, and I'll send it straight over.",
];

/** Labels that fit a lead-collecting button, for the Collect leads step. */
export const SIGNUP_BUTTON_SUGGESTIONS = ['Get started', 'Sign up', 'Join the waitlist', 'Count me in'];

export const COMMENT_REPLY_SUGGESTIONS = [
  'Just sent it to your DMs! 📩',
  'Check your inbox 😊',
  'Sent! Let me know what you think 💬',
];

const KEYWORDS_QUESTION: ZapGoalQuestion = {
  id: 'keywords',
  kind: 'keywords',
  stepLabel: 'Word',
  title: 'Which word will people comment?',
  help: 'When someone comments this word on your post, your Zap starts. Tap one or type your own.',
  placeholder: 'e.g. LINK',
  suggestions: KEYWORD_SUGGESTIONS,
};

/* The card that arrives before your message, and the button on it. Both are
   pre-filled, both are editable, both offer the same presets the full form had —
   a default is a starting point, never a fixed value. */
const OPENER_QUESTION: ZapGoalQuestion = {
  id: 'opener',
  kind: 'opener',
  stepLabel: 'Card',
  title: 'The card they get first',
  help: 'They tap the button, then your message arrives. Edit the words or pick one of ours.',
  optional: true,
};

const DELIVERY_QUESTION: ZapGoalQuestion = {
  id: 'delivery',
  kind: 'delivery',
  stepLabel: 'Message',
  title: 'What should they get?',
  help: 'The message that lands in their inbox. Add a link and it becomes a button.',
  optional: true,
};

const DELIVERY_QUESTION_LINK: ZapGoalQuestion = {
  ...DELIVERY_QUESTION,
  title: 'What should they get?',
  help: 'Your link arrives as a tappable button, with whatever you write above it.',
  linkRequired: true,
  optional: false,
};

/* Shown only when the creator opted into growth, so it never offers "send to
   everyone" — only how firm the ask is. Recommended answer first, pre-selected.
   Because it cannot switch the gate off, it can never remove itself. */
const GROW_FOLLOW_QUESTION: ZapGoalQuestion = {
  id: 'follow_gate',
  kind: 'choice',
  stepLabel: 'Who',
  title: 'Who should get your message?',
  choices: [
    {
      value: 'hard',
      title: 'Only people who follow me',
      description: 'Best for growing. We ask them to follow, then send it.',
    },
    {
      value: 'soft',
      title: 'Everyone who asks',
      description: 'We still ask them to follow, but send it either way.',
    },
  ],
};

/* The editing variant. Creating asks about growth on its own screen before the
   posts; editing skips those intros, so this question carries the "off" option
   instead. It is always present when editing, so choosing "off" only removes
   the Card step that follows it — never the step being answered. */
const EDIT_FOLLOW_GATE_QUESTION: ZapGoalQuestion = {
  id: 'follow_gate',
  kind: 'choice',
  // Same label and question as the creating version — the ONLY difference is
  // the extra "everyone" option, which editing needs because it has no growth
  // screen in front of it. A different chip name here made the two flows look
  // like different features.
  stepLabel: 'Who',
  title: 'Who should get your message?',
  optional: true,
  choices: [
    {
      value: 'off',
      title: 'Everyone who comments',
      description: 'Anyone who comments gets it, follower or not.',
    },
    {
      value: 'soft',
      title: 'Ask them to follow',
      description: 'We ask first, but send it either way.',
    },
    {
      value: 'hard',
      title: 'Only if they follow',
      description: 'Best for growing. They follow you, then it is sent.',
    },
  ],
};

/* Collect leads asks one thing first: do you already have a form, or shall we
   build you one? Everything else on the screen follows from that answer. */
const SIGNUP_QUESTION: ZapGoalQuestion = {
  id: 'signup',
  kind: 'signup',
  stepLabel: 'Details',
  title: 'Where do they leave their details?',
  help: 'Send them to a link you already have, or build a form right here.',
};

/* Multiple replies, rotated at random — the whole point is that a hundred
   commenters do not all see the same sentence under your post. */
const REPLIES_QUESTION: ZapGoalQuestion = {
  id: 'replies',
  kind: 'replies',
  stepLabel: 'Reply',
  title: 'Reply under their comment too?',
  help: 'Everyone can see this on your post. Leave it empty to reply in DMs only.',
  suggestions: COMMENT_REPLY_SUGGESTIONS,
  optional: true,
};

/* Every template ends here: the preview and a plain-English recap, so the last
   thing before Create is "this is what will happen", not another form field. */
const REVIEW_QUESTION: ZapGoalQuestion = {
  id: 'review',
  kind: 'review',
  stepLabel: 'Done',
  title: 'All set — here is what happens',
  optional: true,
};

/* Question order follows the CONVERSATION, not the data model: a follower
   comments, sees your public reply under it, then the card arrives in their
   DMs, and your message lands last. The public reply used to be asked for at
   the end, which contradicted the preview drawn on the same screen — it shows
   that reply directly beneath the comment. */
/* Question order follows the CONVERSATION, not the data model: a follower
   comments, sees your public reply under it, then the card arrives in their
   DMs, and your message lands last. The public reply used to be asked for at
   the end, which contradicted the preview drawn on the same screen — it shows
   that reply directly beneath the comment. */
export const ZAP_GOAL_TEMPLATES: ZapGoalTemplate[] = [
  {
    id: 'link',
    title: 'Share my link',
    tagline: 'Anyone who asks gets it in their DMs',
    questions: [
      KEYWORDS_QUESTION,
      REPLIES_QUESTION,
      GROW_FOLLOW_QUESTION,
      OPENER_QUESTION,
      { ...DELIVERY_QUESTION_LINK, title: 'Which link should they get?' },
      REVIEW_QUESTION,
    ],
  },
  {
    id: 'sell',
    // The id stays 'sell' — it is what rules and saved Zaps already reference —
    // but a creator doing this is sharing affiliate links, not running a shop.
    title: 'Earn affiliate revenue',
    tagline: 'Shoppable product cards in their DMs',
    requires: 'zap_link',
    questions: [
      KEYWORDS_QUESTION,
      REPLIES_QUESTION,
      GROW_FOLLOW_QUESTION,
      OPENER_QUESTION,
      {
        id: 'products',
        kind: 'products',
        stepLabel: 'Product',
        title: 'Which product are you sharing?',
        help: 'Paste its link — we will show its picture and price for you.',
      },
      REVIEW_QUESTION,
    ],
  },
  {
    id: 'faq',
    title: 'Answer a common question',
    tagline: 'Reply publicly and in DMs, instantly',
    questions: [
      KEYWORDS_QUESTION,
      { ...REPLIES_QUESTION, title: 'What should you say under their comment?' },
      GROW_FOLLOW_QUESTION,
      OPENER_QUESTION,
      { ...DELIVERY_QUESTION, title: 'What should the message say?', help: 'This goes to their inbox the moment they comment.' },
      REVIEW_QUESTION,
    ],
  },
  {
    id: 'leads',
    title: 'Collect leads',
    tagline: 'Their details come straight to you',
    // Paid, like 'sell'. Capturing leads — whether into a built-in form or a
    // Google Form — is a Trial/Premium feature, so the card carries the same
    // Upgrade badge rather than letting someone build a Zap they cannot run.
    requires: 'zap_forms',
    questions: [
      KEYWORDS_QUESTION,
      REPLIES_QUESTION,
      GROW_FOLLOW_QUESTION,
      OPENER_QUESTION,
      SIGNUP_QUESTION,
      REVIEW_QUESTION,
    ],
  },
];

/**
 * Does this goal send a card before the message?
 *
 * Only a follow gate does. "Share my link", "Earn affiliate revenue" and
 * "Collect leads" have nothing to ask the follower — the thing they commented for can
 * just arrive — so an opener card there is a tap between them and their link.
 *
 * (The tap does have one technical merit: it counts as a reply from them, which
 * opens Instagram's 24-hour messaging window. Creators who want that for a
 * specific Zap can still switch the rule to a button flow under Change advanced
 * settings.)
 */
export const templateUsesOpener = (
  _template: ZapGoalTemplate,
  entitlements: any,
  answers?: ZapGoalAnswers
): boolean =>
  isFollowGateAllowed(entitlements) && !!answers && answers.follow_gate !== 'off';

export const getGoalTemplate = (id: ZapGoalId | null): ZapGoalTemplate | undefined =>
  ZAP_GOAL_TEMPLATES.find((t) => t.id === id);

/** true when the plan this creator is on can run the template. */
export const isGoalAvailable = (template: ZapGoalTemplate, entitlements: any): boolean => {
  if (template.requires === 'follow_gate') return isFollowGateAllowed(entitlements);
  if (template.requires === 'zap_link') return limitAsNumber(entitlements?.zap_link_limit, 0) > 0;
  if (template.requires === 'zap_forms') return !!entitlements?.zap_forms_enabled;
  return true;
};

/**
 * The questions actually worth asking for this goal on this plan.
 *
 * The opener card (dm_flow_mode !== 'instant') is a paid feature. On a plan
 * without it the templates still work — they just deliver instantly — so the
 * question about follow strictness would be asking about something that cannot
 * happen, and is dropped.
 */
export const goalQuestions = (
  template: ZapGoalTemplate,
  entitlements: any,
  answers?: ZapGoalAnswers,
  opts?: { editing?: boolean }
): ZapGoalQuestion[] => {
  const canUseFollowGate = isFollowGateAllowed(entitlements);

  // Editing has no goal or growth screen in front of it, so the gate question
  // is always offered there — with its own "everyone" option.
  if (opts?.editing) {
    return template.questions
      .map((q) => (q.id === 'follow_gate' ? EDIT_FOLLOW_GATE_QUESTION : q))
      .filter((q) => {
        if (q.id === 'follow_gate') return canUseFollowGate;
        if (q.id === 'opener') return templateUsesOpener(template, entitlements, answers);
        return true;
      });
  }

  return template.questions.filter((q) => {
    // Both the follow choice and the opener card depend on the paid opener.
    // Without it every template delivers instantly, so asking about a card that
    // will never be sent would be asking about nothing.
    //
    // Growth gets a step of its own, skippable; the card it needs appears only
    // once that step says yes.
    // Both of these belong to growth, which is opted into on its own wizard
    // step: without that opt-in there is no follow ask to tune and no card to
    // write.
    if (q.id === 'follow_gate') return canUseFollowGate && !!answers && answers.follow_gate !== 'off';
    // The card only exists once a gate is switched on. It sits AFTER the gate
    // question, so switching the gate never renumbers the step being answered.
    if (q.id === 'opener') return templateUsesOpener(template, entitlements, answers);
    return true;
  });
};

/** Has this question been answered well enough to move on? */
export const isGoalQuestionAnswered = (
  question: ZapGoalQuestion,
  answers: ZapGoalAnswers,
  productCount: number
): boolean => {
  if (question.optional) return true;
  switch (question.id) {
    case 'keywords':
      return answers.keywords.length > 0;
    case 'delivery':
      // Only a goal built around the link insists on one; the message itself
      // always has a working default.
      return question.linkRequired ? /^https?:\/\/\S+$/i.test(answers.link_url.trim()) : true;
    case 'signup':
      // A built form needs a title and at least one question; a borrowed link
      // just needs to be a link.
      return answers.signup_mode === 'form'
        ? answers.form_title.trim().length > 0 && answers.form_fields.length > 0
        : /^https?:\/\/\S+$/i.test(answers.link_url.trim());
    case 'products':
      return productCount > 0;
    case 'opener':
    case 'replies':
    case 'follow_gate':
    case 'review':
      return true; // pre-filled or genuinely optional
    default:
      return true;
  }
};

/** A name the creator never has to type. Editable later under Customise. */
export const buildAutomationName = (template: ZapGoalTemplate, answers: ZapGoalAnswers): string => {
  const keyword = answers.keywords[0];
  return keyword ? `${template.title} · ${keyword}` : template.title;
};

/**
 * Turns the answers into the rule the full form would have produced by hand.
 *
 * `canUseFollowGate` decides whether the opener card is available at all: on a
 * plan without it every template falls back to 'instant' delivery, exactly as
 * StepConfigureRules does when it builds a rule (`canUseFollowGate ? mode : 'instant'`).
 */
export const buildRuleFromGoal = (
  template: ZapGoalTemplate,
  answers: ZapGoalAnswers,
  opts: { entitlements: any; zapLinks?: any[]; ruleId?: number }
): AutomationRuleEntry => {
  const canUseFollowGate = isFollowGateAllowed(opts.entitlements);
  const message = answers.message.trim();
  const linkUrl = answers.link_url.trim();
  const replies = answers.comment_replies.map((r) => r.trim()).filter(Boolean);

  /* Delivery mode follows the gate, whatever the goal is. Asking them to follow
     is a modifier: a product carousel or a lead form can sit behind one exactly
     as the Grow-my-followers shortcut does. With no gate, content the follower
     already asked for arrives without a tap in the way. */
  let dmFlowMode: AutomationRuleEntry['dm_flow_mode'] = 'instant';
  if (answers.follow_gate === 'hard') dmFlowMode = 'hard_follow';
  else if (answers.follow_gate === 'soft') dmFlowMode = 'soft_follow';
  if (!canUseFollowGate) dmFlowMode = 'instant';

  const isZapLink = template.id === 'sell';
  const usesOpener = dmFlowMode !== 'instant';

  return {
    new_rule: true,
    ig_automation_rule_id: opts.ruleId ?? 1,
    rule_type: isZapLink ? 'zap_link' : 'auto_reply',
    keywords: answers.keywords,
    replies,
    // Still a fallback, for a creator who clears the box entirely: a rule with
    // no DM text is rejected on save.
    message_text: isZapLink ? '' : (message || DEFAULT_DM_MESSAGE),
    // More than one reply is rotated so the post does not read like a robot.
    // This was never a setting: the full form's switch was rendered
    // `disabled={true}` purely to say so, and a single reply has nothing to
    // rotate. Derived, so no UI can put it out of step with the reply count.
    scope: replies.length > 1 ? 'multiple' : 'single',
    random_reply: replies.length > 1,
    status: 'Live',
    is_active: true,
    trigger_count: 0,
    zap_links: isZapLink ? (opts.zapLinks || []) : [],
    dm_flow_mode: dmFlowMode,
    // Opener copy only matters when an opener is actually sent.
    opener_message: usesOpener ? answers.opener_message.trim() : '',
    opener_button_label: usesOpener ? answers.opener_button_label.trim().slice(0, 20) : '',
    follow_ask_message:
      dmFlowMode === 'soft_follow' || dmFlowMode === 'hard_follow'
        ? answers.follow_ask_message.trim()
        : '',
    // A link becomes a button under the DM (auto_reply only, both fields or neither).
    button_label: !isZapLink && linkUrl ? (answers.link_label.trim() || 'Open link').slice(0, 20) : '',
    button_url: !isZapLink && linkUrl ? linkUrl : '',
    ...(isZapLink ? zapCtaFields(answers) : {
      zap_cta_label: '',
      zap_cta_url: '',
      zap_cta2_label: '',
      zap_cta2_url: '',
    }),
  };
};

/** Applies the all-or-nothing contract the server enforces for the card buttons. */
const zapCtaFields = (answers: ZapGoalAnswers) => {
  const label1 = answers.zap_cta_label.trim().slice(0, 20);
  const url1 = answers.zap_cta_url.trim();
  const first = label1 && url1 ? { zap_cta_label: label1, zap_cta_url: url1 } : { zap_cta_label: '', zap_cta_url: '' };

  const label2 = answers.zap_cta2_label.trim().slice(0, 20);
  const url2 = answers.zap_cta2_url.trim();
  // A second button with no first would render as the first, so it is only kept
  // when the first is complete.
  const second =
    first.zap_cta_label && label2 && url2
      ? { zap_cta2_label: label2, zap_cta2_url: url2 }
      : { zap_cta2_label: '', zap_cta2_url: '' };

  return { ...first, ...second };
};

/**
 * Plain-English account of everything the template decided on the creator's
 * behalf — shown before Create so nothing is silently preset.
 */
export const summariseGoalRule = (rule: AutomationRuleEntry, keywords: string[]): string[] => {
  const lines: string[] = [];
  lines.push(
    keywords.length
      ? `Someone comments ${keywords.map((k) => `“${k}”`).join(' or ')}`
      : 'Someone comments your keyword'
  );
  if (rule.replies?.length === 1) {
    lines.push(`You reply under their comment: “${rule.replies[0]}”`);
  } else if ((rule.replies?.length || 0) > 1) {
    lines.push(`You reply under their comment, rotating ${rule.replies.length} different replies`);
  }
  if (rule.dm_flow_mode === 'button') {
    lines.push(`They get a card with a “${rule.opener_button_label}” button`);
  } else if (rule.dm_flow_mode === 'soft_follow') {
    lines.push(`They are asked to follow you — the message is sent either way`);
  } else if (rule.dm_flow_mode === 'hard_follow') {
    lines.push(`They are asked to follow you — the message is sent once they do`);
  }
  if (rule.rule_type === 'zap_link') {
    lines.push(`Your product card lands in their DMs`);
  } else {
    lines.push(`Your DM lands${rule.button_url ? ' with a tappable link button' : ''}`);
  }
  return lines;
};

/* ---------------------------------------------------------------------------
   PREVIEW

   The fastest way to explain "opener card", "follow gate" and "DM button" is to
   stop explaining them and show the conversation instead. Both platforms render
   this same list of bubbles, and it is derived from the rule that will actually
   be saved — so the preview cannot drift from the behaviour.
   --------------------------------------------------------------------------- */

export type ZapPreviewStep =
  | { kind: 'comment'; text: string }
  | { kind: 'reply'; text: string }
  | { kind: 'dm'; text: string }
  | { kind: 'button'; text: string }
  | { kind: 'carousel'; cards: ZapPreviewCard[] }
  | { kind: 'form'; title: string; description: string; fields: string[] };

/** One element of the generic-template carousel Instagram actually delivers. */
export interface ZapPreviewCard {
  image: string;
  title: string;
  subtitle: string;
  buttons: string[];
}

/**
 * Builds the cards exactly as public.ts does when it posts the carousel, so the
 * preview is the delivered payload rather than an artist's impression: same
 * image fallback order, same 80-character truncation, same
 * "Shop on <brand>" / "Open" / "Shop Now" button rule, same 10-card ceiling.
 */
const buildPreviewCards = (zapLinks: any[], rule: AutomationRuleEntry): ZapPreviewCard[] => {
  const extras = [
    rule.zap_cta_label && rule.zap_cta_url ? rule.zap_cta_label : '',
    rule.zap_cta2_label && rule.zap_cta2_url ? rule.zap_cta2_label : '',
  ].filter(Boolean) as string[];

  if (!zapLinks.length) {
    return [{ image: '', title: 'Your product', subtitle: '', buttons: ['Shop Now', ...extras] }];
  }

  return zapLinks.slice(0, 10).map((link: any) => ({
    image: (link.product_image_url || link.manual_product_image_url || '').trim(),
    title: (link.product_name || 'Product').substring(0, 80),
    subtitle: (link.brand_name || '').substring(0, 80),
    buttons: [
      link.doc_type
        ? 'Open'
        : (link.brand_name ? `Shop on ${link.brand_name}`.substring(0, 20) : 'Shop Now'),
      ...extras,
    ],
  }));
};

export const buildPreviewSteps = (
  template: ZapGoalTemplate,
  answers: ZapGoalAnswers,
  opts: { entitlements: any; zapLinks?: any[] }
): ZapPreviewStep[] => {
  const rule = buildRuleFromGoal(template, answers, {
    entitlements: opts.entitlements,
    zapLinks: opts.zapLinks,
  });
  const steps: ZapPreviewStep[] = [];

  steps.push({ kind: 'comment', text: answers.keywords[0] || 'LINK' });

  if (rule.replies?.length) steps.push({ kind: 'reply', text: rule.replies[0] });

  const gated = rule.dm_flow_mode === 'soft_follow' || rule.dm_flow_mode === 'hard_follow';
  if (gated) {
    steps.push({ kind: 'dm', text: rule.follow_ask_message || '' });
    steps.push({ kind: 'button', text: rule.opener_button_label || 'Send Link Now' });
  } else if (rule.dm_flow_mode === 'button') {
    steps.push({ kind: 'dm', text: rule.opener_message || '' });
    steps.push({ kind: 'button', text: rule.opener_button_label || 'Send Link Now' });
  }

  if (rule.rule_type === 'zap_link') {
    // The extra buttons live ON each card, not under the thread — that is how
    // Instagram renders a generic template, and showing them as loose bubbles
    // misrepresented where the follower taps.
    steps.push({ kind: 'carousel', cards: buildPreviewCards(opts.zapLinks || [], rule) });
  } else {
    steps.push({ kind: 'dm', text: rule.message_text });

    /* A form built here has no URL until Create Automation saves it, so
       buildRuleFromGoal leaves button_label/url empty and the preview would
       show a message with nothing to tap. Drawn from the answers instead: the
       button they will actually get, and the form it opens. */
    if (answers.signup_mode === 'form') {
      steps.push({ kind: 'button', text: formButtonLabel(answers) });
      steps.push({
        kind: 'form',
        title: answers.form_title.trim() || 'Your form',
        description: answers.form_description.trim(),
        fields: answers.form_fields.map((f) => f.label).filter(Boolean),
      });
    } else if (rule.button_url) {
      steps.push({ kind: 'button', text: rule.button_label || 'Open link' });
    }

  }

  return steps;
};

/** One line saying who ends up receiving the message — the thing people ask about. */
export const previewAudienceNote = (_template: ZapGoalTemplate, answers: ZapGoalAnswers): string => {
  if (answers.follow_gate === 'hard') return 'Only people who follow you get the message.';
  if (answers.follow_gate === 'soft') return 'Everyone gets it — they are just asked to follow first.';
  return 'Everyone who comments gets this.';
};


/* ---------------------------------------------------------------------------
   RECAP FIELDS

   The guided questions ask for the two or three things that are genuinely the
   creator's. Everything else a rule can hold — the opener wording, the button
   label, extra public replies — is still theirs to change; it just lives on the
   recap, one tap away, instead of being asked for up front or hidden behind the
   full form. Nothing a rule supports is unreachable from the guided flow.
   --------------------------------------------------------------------------- */

export type ZapRecapFieldId =
  | 'keywords'
  | 'comment_replies'
  | 'follow_ask_message'
  | 'opener_message'
  | 'opener_button_label'
  | 'message'
  | 'link'
  | 'form_summary'
  | 'card_buttons';

export interface ZapRecapField {
  id: ZapRecapFieldId;
  label: string;
  hint?: string;
  kind: 'chips' | 'replies' | 'text' | 'short' | 'link' | 'form_summary' | 'card_buttons';
  /** Chip suggestions offered inside the editor. */
  suggestions?: string[];
  maxLength?: number;
}

export const recapFields = (
  template: ZapGoalTemplate,
  answers: ZapGoalAnswers,
  entitlements: any
): ZapRecapField[] => {
  const canUseFollowGate = isFollowGateAllowed(entitlements);
  const gated = canUseFollowGate && answers.follow_gate !== 'off';
  const usesOpener = templateUsesOpener(template, entitlements, answers);
  const fields: ZapRecapField[] = [];

  fields.push({
    id: 'keywords',
    label: 'Words that start it',
    hint: 'Anyone commenting one of these sets your Zap off.',
    kind: 'chips',
    suggestions: KEYWORD_SUGGESTIONS,
  });

  fields.push({
    id: 'comment_replies',
    label: 'Your public reply',
    hint: 'Posted under their comment. Add a few and we rotate them.',
    kind: 'replies',
    suggestions: COMMENT_REPLY_SUGGESTIONS,
  });

  if (gated) {
    fields.push({
      id: 'follow_ask_message',
      label: 'How you ask them to follow',
      kind: 'text',
      suggestions: FOLLOW_ASK_SUGGESTIONS,
    });
  } else if (usesOpener) {
    fields.push({
      id: 'opener_message',
      label: 'The card that arrives first',
      kind: 'text',
      suggestions: OPENER_MESSAGE_SUGGESTIONS,
    });
  }

  if (usesOpener) {
    fields.push({
      id: 'opener_button_label',
      label: 'Button they tap',
      hint: 'Up to 20 characters.',
      kind: 'short',
      suggestions: OPENER_BUTTON_SUGGESTIONS,
      maxLength: 20,
    });
  }

  if (template.id !== 'sell') {
    fields.push({
      id: 'message',
      label: 'The message they get',
      kind: 'text',
      suggestions: MESSAGE_SUGGESTIONS,
    });

    if (answers.signup_mode === 'form') {
      // Read-only here on purpose: the builder lives on the Details step, and a
      // second copy of it inside the recap would be two places to edit one thing.
      fields.push({
        id: 'form_summary',
        label: 'Your form',
        hint: 'Change it on the Details step.',
        kind: 'form_summary',
      });
    } else {
      fields.push({
        id: 'link',
        label: answers.link_url.trim() ? 'Link button' : 'Add a link button',
        hint: 'A tappable button under your message.',
        kind: 'link',
        maxLength: 20,
      });
    }
  } else {
    fields.push({
      id: 'card_buttons',
      label: 'Extra buttons on the card',
      hint: 'Up to two, on top of the product link — your shop, your bio page.',
      kind: 'card_buttons',
      maxLength: 20,
    });
  }

  return fields;
};

/** What a recap row shows when it is closed. */
export const recapFieldValue = (field: ZapRecapField, answers: ZapGoalAnswers): string => {
  switch (field.id) {
    case 'keywords':
      return answers.keywords.join(', ') || 'Not set';
    case 'comment_replies': {
      const replies = answers.comment_replies.filter((r) => r.trim());
      if (!replies.length) return 'None — nothing is posted publicly';
      return replies.length === 1 ? replies[0] : `${replies.length} replies, rotated`;
    }
    case 'link':
      return answers.link_url.trim() ? `${answers.link_label || 'Open link'} → ${answers.link_url}` : 'None';
    case 'form_summary': {
      const count = answers.form_fields.length;
      const title = answers.form_title.trim() || 'Untitled form';
      return `${title} · ${count} question${count === 1 ? '' : 's'}`;
    }
    case 'follow_ask_message':
      return answers.follow_ask_message || 'Not set';
    case 'opener_message':
      return answers.opener_message || 'Not set';
    case 'opener_button_label':
      return answers.opener_button_label || 'Send Link Now';
    case 'message':
      return answers.message || 'Here you go! 🙌';
    case 'card_buttons': {
      const labels = [answers.zap_cta_label, answers.zap_cta2_label].map((l) => l.trim()).filter(Boolean);
      return labels.length ? labels.join(', ') : 'None';
    }
    default:
      return '';
  }
};


/**
 * What happens with the replies they have written so far, said plainly.
 *
 * Rotation is not a setting to switch on — it is simply what happens once there
 * is more than one reply. Saying so at one reply is also the only hint a creator
 * gets that adding a second is worth doing.
 */
export const repliesRotationNote = (replies: string[]): string | null => {
  const count = replies.filter((r) => r.trim()).length;
  if (count === 0) return null;
  if (count === 1) return "Everyone who comments sees this same reply. Add another and we'll mix them up.";
  return `We'll pick one of these ${count} at random each time, so your post never looks automated.`;
};


/* ---------------------------------------------------------------------------
   FORM BUILDER HELPERS
   --------------------------------------------------------------------------- */

/** A url-safe handle for the public page, derived from the form's own title. */
export const buildFormSlug = (title: string, existing?: string): string => {
  if (existing) return existing;
  const base = String(title || 'signup')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 48) || 'signup';
  // Four random characters keep two creators naming a form "Waitlist" from
  // colliding; the server still enforces uniqueness and says so if it happens.
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}-${suffix}`;
};

/**
 * What the DM button says when it opens a form built here.
 *
 * link_label defaults to 'Open link', which is right for a link somebody pasted
 * and wrong for a lead form — so an untouched default becomes DEFAULT_SIGNUP_BUTTON,
 * while anything the creator actually typed is left alone.
 */
export const formButtonLabel = (answers: ZapGoalAnswers): string => {
  const label = answers.link_label?.trim() || '';
  return !label || label === 'Open link' ? DEFAULT_SIGNUP_BUTTON : label;
};

/** Adds a custom question with a minted key (labels are editable, keys are not). */
export const newCustomFormField = (label: string): ZapFormFieldDraft => ({
  key: `q_${Math.random().toString(36).slice(2, 8)}`,
  label: label.trim().substring(0, 120),
  type: 'text',
  required: false,
  placeholder: '',
});

/** The payload the save endpoint expects for this response's form. */
export const formSavePayload = (
  answers: ZapGoalAnswers,
  owner: { user_id: any; account_id: any; influencer_id: any }
) => ({
  zap_form_id: answers.zap_form_id || undefined,
  user_id: owner.user_id,
  account_id: owner.account_id,
  influencer_id: owner.influencer_id,
  slug: buildFormSlug(answers.form_title, answers.form_slug),
  title: answers.form_title.trim() || 'Leave your details',
  description: answers.form_description.trim(),
  success_message: answers.form_success_message.trim() || DEFAULT_FORM_SUCCESS,
  fields: answers.form_fields,
  status: 'active',
});


/** Growth needs the paid opener card, whatever the goal. */
export const canAddGrowth = (entitlements: any): boolean => isFollowGateAllowed(entitlements);

/* ---------------------------------------------------------------------------
   EDITING — a saved rule, read back as answers

   A Zap is stored as rules, not as the answers that produced it, so editing one
   used to drop straight into the full form: a creator who built a Zap in four
   plain questions came back to "Configure Automation Rules".

   Every field the guided flow writes can be read back out, so the same screens
   can edit it. What cannot be recovered is which goal was picked — the rule only
   records what it DOES — so the goal is inferred from the rule's shape:

     zap_link                    -> Earn affiliate revenue
     auto_reply with a button    -> Share my link
     auto_reply without a button -> Answer a common question

   A lead Zap pointing at a ClanConnect form reads back as "Share my link" with
   the form's URL intact: the form's questions live in cct_zap_forms, not in the
   rule, so presenting the builder would mean showing an empty one. The link
   still works and is still editable — nothing is lost, it is just edited as a
   link rather than as a form.
   --------------------------------------------------------------------------- */

export interface InferredGoal {
  goal: ZapGoalId;
  answers: ZapGoalAnswers;
  zapLinks: any[];
}

export const inferGoalFromRule = (rule: AutomationRuleEntry): InferredGoal => {
  const answers = emptyGoalAnswers();

  answers.keywords = [...(rule.keywords || [])];
  answers.comment_replies = (rule.replies || []).filter((r) => !!String(r || '').trim());
  answers.message = rule.message_text || DEFAULT_DM_MESSAGE;

  // Opener copy: fall back to the defaults when the saved rule has none, so the
  // fields are never blank.
  answers.opener_message = rule.opener_message || OPENER_MESSAGE;
  answers.opener_button_label = rule.opener_button_label || OPENER_BUTTON;
  answers.follow_ask_message = rule.follow_ask_message || FOLLOW_ASK;

  answers.follow_gate =
    rule.dm_flow_mode === 'hard_follow' ? 'hard'
    : rule.dm_flow_mode === 'soft_follow' ? 'soft'
    : 'off';

  answers.link_url = rule.button_url || '';
  if (rule.button_label) answers.link_label = rule.button_label;

  answers.zap_cta_label = rule.zap_cta_label || '';
  answers.zap_cta_url = rule.zap_cta_url || '';
  answers.zap_cta2_label = rule.zap_cta2_label || '';
  answers.zap_cta2_url = rule.zap_cta2_url || '';

  const goal: ZapGoalId =
    rule.rule_type === 'zap_link' ? 'sell'
    : (rule.button_url || '').trim() ? 'link'
    : 'faq';

  return { goal, answers, zapLinks: rule.zap_links || [] };
};

/**
 * Reads a whole saved automation back into the guided flow.
 *
 * Returns null when the rules disagree about what the Zap is — a Zap holding
 * both a product carousel and a plain reply has no single goal, and forcing one
 * would rewrite the other rule on save. Those keep the full form, which can
 * express anything.
 */
export const inferGoalFromRules = (
  rules: AutomationRuleEntry[]
): { goal: ZapGoalId; responses: ZapGoalAnswers[]; zapLinks: any[][] } | null => {
  const live = (rules || []).filter((r) => r && r.status !== 'Deleted');
  if (!live.length) return null;

  const inferred = live.map(inferGoalFromRule);
  const goal = inferred[0].goal;
  if (inferred.some((i) => i.goal !== goal)) return null;

  return {
    goal,
    responses: inferred.map((i) => i.answers),
    zapLinks: inferred.map((i) => i.zapLinks),
  };
};
